/**
 * Sentry — error tracking del browser (runbook `docs/ops/sentry-setup-runbook.md`).
 *
 * El backend ya reportaba a Sentry desde septiembre de 2026; el frontend no,
 * asi que un error de React en produccion no aparecia en ningun lado. Este
 * modulo cierra ese hueco.
 *
 * **Por que la config se arma en una funcion pura y aparte del `init`:** lo
 * que de verdad hay que blindar no es la llamada al SDK sino el contrato de
 * privacidad (R-SEC-004) — que no se filtre PII en la URL ni en los
 * breadcrumbs. `buildSentryConfig` es testeable sin tocar el SDK ni el estado
 * global del browser, y `initSentry` queda como una linea de pegamento.
 *
 * **Sin DSN no se inicializa nada.** En dev local eso es lo normal y no
 * queremos ruido; en produccion significa volar a ciegas, asi que ahi si se
 * avisa por consola. Es el mismo razonamiento que el `sentry.disabled` del
 * backend (`app/observability.py`) y que el hallazgo H-3: una senal que se
 * verifica mirando el log tiene que emitirse en ambas ramas.
 *
 * **Session Replay queda fuera a proposito.** Graba el DOM, o sea el nombre
 * y el correo del funcionario dentro de la app — justo lo que R-SEC-004
 * prohibe persistir. Ademas el plan free trae 50 replays al mes, que no
 * alcanzan para nada util.
 */

import * as Sentry from "@sentry/react";

/** Subconjunto de `import.meta.env` que le importa a este modulo. */
export interface SentryEnv {
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  VITE_RELEASE_ID?: string;
  MODE?: string;
}

/**
 * Forma minima de lo que este modulo toca de un evento. A proposito sin
 * index signature: asi el `ErrorEvent` real del SDK es asignable a este tipo
 * y `beforeSend` encaja en `Sentry.init` sin castear nada.
 */
interface BreadcrumbLike {
  // `category` no se usa aca; esta para que el tipo describa un breadcrumb
  // real y no sea un "weak type" que TS rechaza contra `{ category }`.
  category?: string;
  data?: Record<string, unknown>;
}

interface SentryEventLike {
  request?: { url?: string };
  breadcrumbs?: BreadcrumbLike[];
}

export interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
  sendDefaultPii: false;
  release?: string;
  beforeSend: <E extends SentryEventLike>(event: E) => E;
}

const DEFAULT_TRACES_SAMPLE_RATE = 0.1;

/**
 * Claves de `breadcrumb.data` que el SDK llena con URLs: `url` para
 * fetch/xhr, `from`/`to` para las navegaciones del router.
 */
const URL_BEARING_BREADCRUMB_KEYS = ["url", "from", "to"] as const;

/**
 * Corta una URL en el primer `?` o `#`.
 *
 * El `#` no es paranoia: Supabase entrega la sesion de `/reset-password` y
 * `/aceptar-invitacion` como `#access_token=...&refresh_token=...`. Cortar
 * solo por `?` dejaria pasar el token entero a Sentry.
 */
function scrubUrl(value: string): string {
  // `split` siempre devuelve al menos un elemento; el `?? value` es para
  // `noUncheckedIndexedAccess`, no porque pueda faltar.
  return value.split(/[?#]/)[0] ?? value;
}

function scrubEvent<E extends SentryEventLike>(event: E): E {
  if (typeof event.request?.url === "string") {
    event.request.url = scrubUrl(event.request.url);
  }

  for (const breadcrumb of event.breadcrumbs ?? []) {
    const data = breadcrumb.data;
    if (!data) continue;
    for (const key of URL_BEARING_BREADCRUMB_KEYS) {
      if (typeof data[key] === "string") {
        data[key] = scrubUrl(data[key]);
      }
    }
  }

  return event;
}

/**
 * Lee el sample rate de tracing. Un valor corrupto o fuera de rango cae al
 * default en vez de romper el arranque: el costo de equivocarse aca es
 * mandar mas o menos trazas, nunca dejar la app sin error tracking.
 */
function parseTracesSampleRate(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === "") return DEFAULT_TRACES_SAMPLE_RATE;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return DEFAULT_TRACES_SAMPLE_RATE;
  }
  return parsed;
}

/**
 * Arma la config de Sentry, o `null` si no hay DSN (dev local).
 */
export function buildSentryConfig(env: SentryEnv): SentryConfig | null {
  const dsn = env.VITE_SENTRY_DSN?.trim();
  if (!dsn) return null;

  return {
    dsn,
    environment: env.MODE ?? "development",
    tracesSampleRate: parseTracesSampleRate(env.VITE_SENTRY_TRACES_SAMPLE_RATE),
    // R-SEC-004: nada de correos, IPs ni headers de sesion.
    sendDefaultPii: false,
    release: env.VITE_RELEASE_ID,
    beforeSend: scrubEvent,
  };
}

/**
 * Inicializa Sentry si hay DSN. Se llama una sola vez, desde `main.tsx`,
 * antes de montar React para que un error de render ya quede capturado.
 */
export function initSentry(env: SentryEnv = import.meta.env): void {
  const config = buildSentryConfig(env);

  if (!config) {
    if (env.MODE === "production") {
      // En prod esto significa que los errores del browser no llegan a
      // ningun lado. En dev es esperado y no se dice nada.
      console.warn("[sentry] deshabilitado: falta VITE_SENTRY_DSN");
    }
    return;
  }

  Sentry.init({
    ...config,
    integrations: [Sentry.browserTracingIntegration()],
  });
}
